import { useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Send, Paperclip, X as XIcon } from "lucide-react";
import { issuesApi, type Issue, type IssueComment, type IssueAttachment } from "@/api/issues";

/**
 * BlockedBanner — banner de ação pra issues em status=blocked.
 *
 * Mostra o último comentário do agente, permite anexar arquivos (foto do
 * expert, asset visual etc) + escrever resposta. Ao clicar Desbloquear:
 *  1. Sobe os arquivos como attachments da issue
 *  2. Inclui as URLs públicas dos attachments na message
 *  3. Chama /unblock que muda status pra todo + acorda agente
 */

export function BlockedBanner({
  issue,
  onSuccess,
}: {
  issue: Issue;
  onSuccess?: () => void;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const commentsQuery = useQuery({
    queryKey: ["issues", issue.id, "comments"],
    queryFn: () => issuesApi.comments(issue.id),
    enabled: issue.status === "blocked",
  });

  const unblock = useMutation({
    mutationFn: async () => {
      // 1. Sobe os arquivos como attachments (paralelo)
      const uploaded: IssueAttachment[] = await Promise.all(
        files.map((f) => issuesApi.uploadAttachment(issue.companyId, issue.id, f)),
      );

      // 2. Constrói a message com URLs absolutas dos attachments
      const origin = window.location.origin;
      const filesBlock =
        uploaded.length > 0
          ? "\n\nArquivos anexados:\n" +
            uploaded
              .map(
                (a) =>
                  `- ${a.originalFilename ?? "anexo"} (${a.contentType}): ${origin}${a.contentPath}`,
              )
              .join("\n")
          : "";

      const fullMessage = (message.trim() + filesBlock).trim();
      if (!fullMessage) {
        throw new Error("Anexe um arquivo ou escreva uma mensagem.");
      }

      // 3. Chama /unblock
      return issuesApi.unblock(issue.id, fullMessage);
    },
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ["issues", issue.id] });
      queryClient.invalidateQueries({
        queryKey: ["companies", issue.companyId, "issues"],
      });
      setMessage("");
      setFiles([]);
      onSuccess?.();
      navigate(`/execucao/${updated.id}`);
    },
  });

  if (issue.status !== "blocked") return null;

  const allComments = commentsQuery.data ?? [];
  // Mostra o último comentário que NÃO foi escrito pelo usuário — cobre tanto
  // comentários do agente (authorAgentId setado) quanto comentários do sistema
  // (ex.: "Geração de arte falhou: Gemini 503" — ambos os autores null).
  const nonUserComments = allComments.filter(
    (c: IssueComment) => c.authorUserId == null,
  );
  const lastAgentComment = nonUserComments[nonUserComments.length - 1];

  const handleFilesPicked = (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(e.target.files ?? []);
    setFiles((prev) => [...prev, ...picked]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeFile = (idx: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const canSubmit = (message.trim().length > 0 || files.length > 0) && !unblock.isPending;

  return (
    <div
      className="wf-card wf-card--warning"
      style={{ padding: 16, marginBottom: 18 }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: 10,
          marginBottom: 12,
        }}
      >
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: 8,
            background: "rgba(204,51,68,0.12)",
            color: "var(--nd-orange-pun)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AlertTriangle size={16} strokeWidth={1.75} />
        </span>
        <div style={{ flex: 1 }}>
          <div
            className="wf-display"
            style={{
              fontSize: 15,
              fontWeight: 700,
              letterSpacing: "-0.01em",
              color: "var(--wf-ink)",
              marginBottom: 2,
            }}
          >
            Tarefa bloqueada — agente precisa de você
          </div>
          <div style={{ fontSize: 12, color: "var(--wf-ink-2)", lineHeight: 1.5 }}>
            O agente parou porque precisa de informação ou asset que você não
            anexou. Leia o que ele pediu, anexe arquivos ou escreva a resposta,
            e clique em desbloquear.
          </div>
        </div>
      </div>

      {lastAgentComment && (
        <div
          style={{
            padding: 12,
            background: "var(--wf-paper)",
            border: "1px solid var(--wf-ink-5)",
            borderRadius: 8,
            marginBottom: 12,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 600,
              color: "var(--wf-ink-3)",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              marginBottom: 6,
            }}
          >
            O agente disse
          </div>
          <div
            style={{
              fontSize: 13,
              color: "var(--wf-ink)",
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
            }}
          >
            {lastAgentComment.body}
          </div>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <label
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: "var(--wf-ink-3)",
            textTransform: "uppercase",
            letterSpacing: "0.04em",
          }}
        >
          Sua resposta
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Descreva o que o agente precisa saber. Pode anexar arquivos abaixo (imagens, PDFs etc)."
          rows={3}
          style={{
            width: "100%",
            padding: 10,
            border: "1px solid var(--wf-ink-5)",
            borderRadius: 8,
            fontFamily: "var(--wf-font-body)",
            fontSize: 13,
            resize: "vertical",
            background: "var(--wf-paper)",
            lineHeight: 1.55,
          }}
        />

        {/* Lista de arquivos selecionados */}
        {files.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {files.map((f, i) => (
              <div
                key={`${f.name}-${i}`}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "6px 10px",
                  background: "var(--wf-paper)",
                  border: "1px solid var(--wf-ink-5)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              >
                <Paperclip size={12} color="var(--nd-orange)" />
                <span
                  style={{
                    flex: 1,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    color: "var(--wf-ink)",
                  }}
                >
                  {f.name}
                </span>
                <span style={{ fontSize: 11, color: "var(--wf-ink-3)" }}>
                  {(f.size / 1024).toLocaleString("pt-BR", { maximumFractionDigits: 0 })} KB
                </span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  aria-label="Remover"
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: 2,
                    color: "var(--wf-ink-3)",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <XIcon size={12} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Action row */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 10,
          }}
        >
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="wf-btn wf-btn--ghost wf-btn--sm"
            style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
          >
            <Paperclip size={12} /> Anexar arquivo
          </button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,.pdf"
            onChange={handleFilesPicked}
            style={{ display: "none" }}
          />

          <button
            type="button"
            onClick={() => unblock.mutate()}
            disabled={!canSubmit}
            className="wf-btn wf-btn--primary wf-btn--sm"
            style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
          >
            <Send size={12} strokeWidth={2} color="#fff" />
            {unblock.isPending ? "Desbloqueando…" : "Desbloquear e retomar"}
          </button>
        </div>

        {unblock.isError && (
          <div
            style={{
              fontSize: 12,
              color: "var(--nd-orange-pun)",
              padding: 8,
              background: "rgba(204,51,68,0.06)",
              borderRadius: 6,
            }}
          >
            {(unblock.error as Error).message}
          </div>
        )}
      </div>
    </div>
  );
}
